import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api, { grievancesAPI, GrievanceStatus } from '../../services/api';
import { toast } from 'react-hot-toast';
import { Download, Printer } from 'lucide-react';
import {
  downloadGrievanceReport,
  GrievanceDetail,
  printGrievanceReport,
  TimelineEntry,
} from '../../utils/grievanceExport';

export default function OfficerReviews() {
  const [searchParams] = useSearchParams();
  const idFromUrl = searchParams.get('id') || '';

  const [grievanceId, setGrievanceId] = useState(idFromUrl);
  const [timeline, setTimeline] = useState<TimelineEntry[]>([]);
  const [grievance, setGrievance] = useState<GrievanceDetail | null>(null);
  const [recordId, setRecordId] = useState<number | null>(null);
  const [currentStatus, setCurrentStatus] = useState<GrievanceStatus>('pending');
  const [status, setStatus] = useState('submitted');
  const [message, setMessage] = useState('');
  const [office, setOffice] = useState('');
  const [eventDate, setEventDate] = useState('');

  const loadTimeline = async () => {
    if (!grievanceId) return;
    try {
      const [timelineRes, detailsRes] = await Promise.all([
        grievancesAPI.getTimeline(grievanceId),
        api.get(`/grievances/${grievanceId}`),
      ]);
      setTimeline(timelineRes.data?.data || []);
      const data = detailsRes.data?.data || detailsRes.data;
      setGrievance(data ?? null);
      setOffice(data?.office ?? '');
      setRecordId(data?.id ?? null);
      setCurrentStatus(data?.status ?? 'pending');
    } catch (e) {
      setTimeline([]);
      setGrievance(null);
      setRecordId(null);
      setCurrentStatus('pending');
    }
  };

  useEffect(() => {
    loadTimeline();
  }, [grievanceId]);

  // Sync grievanceId from URL when navigating from Grievances page
  useEffect(() => {
    if (idFromUrl && idFromUrl !== grievanceId) {
      setGrievanceId(idFromUrl);
    }
  }, [idFromUrl]);

  const addEntry = async () => {
    if (!grievanceId || !status || !message) {
      toast.error('Please fill grievance ID, status, and message');
      return;
    }
    try {
      await grievancesAPI.addTimeline(grievanceId, { status, message, office, eventDate: eventDate || undefined });
      toast.success('Timeline entry added');
      setMessage('');
      setEventDate('');
      loadTimeline();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to add entry');
    }
  };

  const systemStatuses = [
    { value: 'pending', label: 'Pending' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'resolved', label: 'Resolved' },
    { value: 'rejected', label: 'Rejected' },
  ];

  const statuses = [
    { value: 'submitted', label: 'दर्ता भयो' },
    { value: 'approved', label: 'अनुमोदन भयो' },
    { value: 'monitoring', label: 'अनुगमन हुँदैछ' },
    { value: 'in-progress', label: 'कारवाही हुँदैछ' },
    { value: 'resolved', label: 'समाधान भयो' },
    { value: 'rejected', label: 'अस्वीकृत' },
  ];

  const updateGrievanceStatus = async () => {
    if (!recordId) {
      toast.error('Load a valid grievance first');
      return;
    }
    try {
      await grievancesAPI.updateStatus(recordId, { status: currentStatus });
      toast.success('Grievance status updated');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to update status');
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">Reviews / Timeline</h1>

      <div className="bg-white rounded-lg shadow p-4 mb-6 space-y-3">
        <div className="flex gap-3 items-center">
          <input
            className="input"
            placeholder="Grievance ID (e.g., CM_0001)"
            value={grievanceId}
            onChange={(e) => setGrievanceId(e.target.value)}
          />
          <button className="btn-outline" onClick={loadTimeline}>Load</button>
          {grievance && (
            <>
              <button
                type="button"
                className="btn-outline inline-flex items-center gap-1"
                onClick={() => printGrievanceReport(grievance, timeline)}
              >
                <Printer className="h-4 w-4" /> Print
              </button>
              <button
                type="button"
                className="btn-primary inline-flex items-center gap-1"
                onClick={async () => {
                  try {
                    await downloadGrievanceReport(grievance, timeline);
                    toast.success('PDF downloaded');
                  } catch {
                    toast.error('Failed to generate PDF');
                  }
                }}
              >
                <Download className="h-4 w-4" /> Download PDF
              </button>
            </>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <label className="label">Grievance Status</label>
            <select className="input" value={currentStatus} onChange={(e) => setCurrentStatus(e.target.value as GrievanceStatus)}>
              {systemStatuses.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <button className="btn-primary" onClick={updateGrievanceStatus} disabled={!recordId}>Update Status</button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="label">Status</label>
            <select className="input" value={status} onChange={(e) => setStatus(e.target.value)}>
              {statuses.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Date</label>
            <input type="datetime-local" className="input" value={eventDate} onChange={(e) => setEventDate(e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <label className="label">Office</label>
            <input className="input" value={office} onChange={(e) => setOffice(e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <label className="label">Message</label>
            <textarea className="input" rows={3} placeholder="सन्देश" value={message} onChange={(e) => setMessage(e.target.value)} />
          </div>
        </div>

        <div>
          <button className="btn-primary" onClick={addEntry}>Add Entry</button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="border-b px-4 py-2 text-sm text-gray-600">{timeline.length} entries</div>
        <div className="divide-y">
          {timeline.map(item => (
            <div key={item.id} className="p-4">
              <div className="text-sm text-gray-500">{new Date(item.eventDate).toLocaleString()}</div>
              <div className="font-semibold capitalize">{item.status}</div>
              <div className="text-gray-800 whitespace-pre-wrap">{item.message}</div>
              {item.office && <div className="text-sm text-gray-600">{item.office}</div>}
            </div>
          ))}
          {timeline.length === 0 && (
            <div className="p-6 text-center text-gray-500 text-sm">No timeline entries</div>
          )}
        </div>
      </div>
    </div>
  );
}


